<template>
    <p class="mb-4 text-center">
        <em>Thank you for confirming {{ props.email }}.</em>
    </p>
    <div class="px-4 py-8 mb-12 bg-white border shadow-lg sm:rounded-lg sm:px-10">
        <div class="mt-2">
            <h2 class="mb-2 text-xl font-semibold text-center text-gray-900">Welcome to StageAgent!</h2>
            <p class="mb-2 text-sm text-center">Please complete the steps below to finish creating your account</p>
            <p class="mt-6 mb-2 font-semibold text-center text-clay-900">Who is this account for?</p>
            <div class="flex justify-center mt-2 min-w-100">
                <fieldset class="min-w-full" aria-label="Who is this account for?">
                    <div class="grid grid-cols-2 p-1 font-semibold text-center rounded-full gap-x-1 text-xs/5 ring-1 ring-gray-200 ring-inset"
                         id="headlessui-radiogroup-:r0:" role="radiogroup">
                        <span @click.prevent="request.accountIsForChild = false"
                              class="cursor-pointer rounded-full px-2.5 py-1 text-clay-800"
                              :class="!request.accountIsForChild ? 'bg-electric-600 text-white' : ''"
                              id="headlessui-radio-:r1:" role="radio" aria-checked="true" tabindex="0">Myself</span>
                        <span
                                @click.prevent="request.accountIsForChild = true"
                                class="cursor-pointer rounded-full px-2.5 py-1 text-clay-800"
                                :class="request.accountIsForChild ? 'bg-electric-600 text-white' : ''"
                                id="headlessui-radio-:r2:" role="radio" aria-checked="false"
                                tabindex="-1">My Child</span></div>
                </fieldset>
            </div>
            <div class="mt-6">
                <ErrorSummary :response-status="responseStatus"/>
            </div>
        </div>

        <!-- if Self-->
        <div v-if="!request.accountIsForChild" class="mt-8">

            <div id="form-signup"
                 role="form"
                 class="mt-6 space-y-6">
                <div>
                    <label for="name" class="block text-sm font-medium text-gray-700">Your Name</label>
                    <div class="mt-1">
                        <input v-model="request.name"
                               id="name"
                               name="name"
                               placeholder="Your first and last name"
                               type="text"
                               autocomplete="name"
                               required
                               autofocus=""
                               class="block w-full px-3 py-2 placeholder-gray-400 border rounded-md shadow-sm appearance-none border-clay-500 focus:outline-hidden focus:ring-slate-500 focus:border-slate-500 sm:text-sm">
                    </div>
                </div>
                <div>

                    <label for="password" class="block text-sm font-medium text-gray-700">
                        Password
                    </label>
                    <div class="mt-1">
                        <input v-model="request.password"
                               id="password"
                               name="password"
                               :type="passwordVisible ? 'text' : 'password'"
                               autocomplete="off"
                               placeholder="Choose a password"
                               required
                               class="block w-full px-3 py-2 placeholder-gray-400 border rounded-md shadow-sm appearance-none border-clay-500 focus:outline-hidden focus:ring-slate-500 focus:border-slate-500 sm:text-sm">
                    </div>
                    <p class="mt-2 text-sm text-clay-800">Your password must be at least 6 characters long and must
                        contain at least one uppercase, one lowercase, and one numerical character.</p>

                </div>

                <div class="flex items-center justify-between">
                    <div class="flex items-center">

                        <input v-model="request.agreeToNewsletters" 
                               id="agreeToNewsletters"
                               name="agreeToNewsletters"
                               type="checkbox"
                               value="true"
                               class="w-4 h-4 rounded border-clay-500 text-slate-800 focus:ring-slate-500">

                        <label for="agreeToNewsletters"
                               class="block ml-2 text-sm text-gray-900">
                                Get emails about theatre news, tips, auditions, and offers. Customize preferences anytime in Account Settings.
                        </label>

                    </div>
                </div>

                <div>
                    <SaButton @click.prevent="createAccount" :loading="isSubmitting" variant="primary">
                        <template #loading>
                            Submitting...
                        </template>
                        Create My Account
                    </SaButton>
                </div>
            </div>
        </div>
        <!-- If Parent -->
        <div v-else class="sm:mt-8">
            <div class="my-4 space-y-3 text-sm">
                <p>Thank you for your interest in creating a StageAgent account on behalf of your child.
                    StageAgent is committed to protecting your child’s privacy and providing a safe,
                    educational, and engaging environment. </p>

                <p>We collect and process your child’s information to provide access to StageAgent services,
                    including personalized profiles, theatre resources, and educational content. </p>

                <p>Parents and legal guardians are responsible for creating and maintaining your child’s
                    profile, visibility settings, reviewing any notifications, messages, invitations or job
                    opportunities. </p>

                <p>Parents and legal guardians must use their own name and email address to create an account.
                    However, you can choose to display your child’s name, images, videos, CV, appearance,
                    credits, and/or skills on your public profile.</p>

                <div class="pb-4 border-b border-gray-900/10">

                    <div class="mt-10 space-y-10">
                        <fieldset>
                            <legend class="font-semibold text-gray-900 text-sm/6">
                                Please review and acknowledge the following:
                            </legend>
                            <div class="mt-6 space-y-3">
                                <div class="flex gap-3">
                                    <div class="flex items-center h-6 shrink-0">
                                        <div class="grid grid-cols-1 group size-4">
                                            <input v-model="agreeToAgeVerify"
                                                   id="age-verify"
                                                   aria-describedby="age-verify"
                                                   name="age-verify" type="checkbox"
                                                   class="col-start-1 row-start-1 bg-white border rounded-sm appearance-none border-clay-500 checked:border-electric-600 checked:bg-electric-600 indeterminate:border-electric-600 indeterminate:bg-electric-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric-600 disabled:border-clay-500 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto">
                                            <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
                                                 viewBox="0 0 14 14" fill="none">
                                                <path class="opacity-0 group-has-checked:opacity-100"
                                                      d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round"
                                                      stroke-linejoin="round"></path>
                                                <path class="opacity-0 group-has-indeterminate:opacity-100"
                                                      d="M3 7H11" stroke-width="2" stroke-linecap="round"
                                                      stroke-linejoin="round"></path>
                                            </svg>
                                        </div>
                                    </div>
                                    <div class="text-sm/6">
                                        <label for="age-verify" class="font-medium text-gray-900">I am the
                                            parent or legal guardian of the child using this account and I
                                            confirm that I am at least 18 years old.

                                        </label>

                                    </div>
                                </div>
                                <div class="flex gap-3">
                                    <div class="flex items-center h-6 shrink-0">
                                        <div class="grid grid-cols-1 group size-4">
                                            <input v-model="agreeToCollectConsent"
                                                   id="consent-collect" aria-describedby="consent-collect"
                                                   name="consent-collect" type="checkbox"
                                                   class="col-start-1 row-start-1 bg-white border rounded-sm appearance-none border-clay-500 checked:border-electric-600 checked:bg-electric-600 indeterminate:border-electric-600 indeterminate:bg-electric-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric-600 disabled:border-clay-500 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto">
                                            <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
                                                 viewBox="0 0 14 14" fill="none">
                                                <path class="opacity-0 group-has-checked:opacity-100"
                                                      d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round"
                                                      stroke-linejoin="round"></path>
                                                <path class="opacity-0 group-has-indeterminate:opacity-100"
                                                      d="M3 7H11" stroke-width="2" stroke-linecap="round"
                                                      stroke-linejoin="round"></path>
                                            </svg>
                                        </div>
                                    </div>
                                    <div class="text-sm/6">
                                        <label for="consent-collect" class="font-medium text-gray-900">I consent
                                            to the collection, use, and storage of my child’s personal
                                            information as outlined in the <a href="/pages/privacy" target="_blank" class="underline">StageAgent Privacy Policy</a>.

                                        </label>

                                    </div>
                                </div>
                                <div class="flex gap-3">
                                    <div class="flex items-center h-6 shrink-0">
                                        <div class="grid grid-cols-1 group size-4">
                                            <input v-model="agreeToCompliance"
                                                   id="compliance" aria-describedby="compliance"
                                                   name="compliance" type="checkbox"
                                                   class="col-start-1 row-start-1 bg-white border rounded-sm appearance-none border-clay-500 checked:border-electric-600 checked:bg-electric-600 indeterminate:border-electric-600 indeterminate:bg-electric-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric-600 disabled:border-clay-500 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto">
                                            <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
                                                 viewBox="0 0 14 14" fill="none">
                                                <path class="opacity-0 group-has-checked:opacity-100"
                                                      d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round"
                                                      stroke-linejoin="round"></path>
                                                <path class="opacity-0 group-has-indeterminate:opacity-100"
                                                      d="M3 7H11" stroke-width="2" stroke-linecap="round"
                                                      stroke-linejoin="round"></path>
                                            </svg>
                                        </div>
                                    </div>
                                    <div class="text-sm/6">
                                        <label for="compliance" class="font-medium text-gray-900">I agree to
                                            manage my child’s account and ensure compliance with 
                                            <a href="/pages/terms" target="_blank"  class="underline">StageAgent’s Terms of Service</a>.

                                        </label>

                                    </div>
                                </div>
                                <div class="flex gap-3">
                                    <div class="flex items-center h-6 shrink-0">
                                        <div class="grid grid-cols-1 group size-4">
                                            <input v-model="agreeToRevokeConsent"
                                                   id="offers" aria-describedby="offers-description"
                                                   name="offers" type="checkbox"
                                                   class="col-start-1 row-start-1 bg-white border rounded-sm appearance-none border-clay-500 checked:border-electric-600 checked:bg-electric-600 indeterminate:border-electric-600 indeterminate:bg-electric-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric-600 disabled:border-clay-500 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto">
                                            <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
                                                 viewBox="0 0 14 14" fill="none">
                                                <path class="opacity-0 group-has-checked:opacity-100"
                                                      d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round"
                                                      stroke-linejoin="round"></path>
                                                <path class="opacity-0 group-has-indeterminate:opacity-100"
                                                      d="M3 7H11" stroke-width="2" stroke-linecap="round"
                                                      stroke-linejoin="round"></path>
                                            </svg>
                                        </div>
                                    </div>
                                    <div class="text-sm/6">
                                        <label for="offers" class="font-medium text-gray-900">I understand that
                                            I can revoke consent at any time by deleting my account in my
                                            Account Settings or by contacting StageAgent customer support at
                                            support@stageagent.com.

                                        </label>

                                    </div>
                                </div>
                            </div>
                        </fieldset>


                    </div>
                </div>
                <div class="pb-4 border-b border-gray-900/10">
                    <h2 class="font-semibold text-gray-900 text-base/7">Digital signature</h2>

                    <div class="grid grid-cols-1 mt-4 gap-x-6 gap-y-4 sm:grid-cols-6">

                        <div class="sm:col-span-6">
                            <label for="first-name" class="block font-medium text-gray-900 text-sm/6">Your
                                Name</label>
                            <div class="mt-2">
                                <input v-model="request.name"
                                       type="text" name="first-name" id="first-name" autocomplete="given-name"
                                       class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-electric-600 sm:text-sm/6"
                                       placeholder="Parent/Guardian Full Legal Name">
                            </div>
                        </div>

                        
                        
                        
                    </div>
                </div>
                <div class="pb-4 border-b border-gray-900/10">
                    <h2 class="font-semibold text-gray-900 text-base/7">Account</h2>

                    <div class="grid grid-cols-1 mt-4 gap-x-6 gap-y-4 sm:grid-cols-6">

                        
                        
                        <div class="sm:col-span-6">
                            <label for="email-address" class="block font-medium text-gray-900 text-sm/6">Your
                                Email</label>
                            <div class="mt-2">
                                <p>{{ props.email }}</p>
                            </div>
                        </div>
                        
                        <div class="sm:col-span-6">
                            <label for="password" class="block text-sm font-medium text-gray-700">
                                Choose a Password.
                            </label>
                            <div class="mt-1">
                                <input v-model="request.password"
                                       id="password"
                                       name="password"
                                       :type="passwordVisible ? 'text' : 'password'"
                                       autocomplete="off"
                                       placeholder="Choose a password"
                                       required
                                       class="block w-full px-3 py-2 placeholder-gray-400 border rounded-md shadow-sm appearance-none border-clay-500 focus:outline-hidden focus:ring-slate-500 focus:border-slate-500 sm:text-sm">
                            </div>
                            <p class="mt-2 text-sm text-clay-800">Your password must be at least 6 characters long and must
                                contain at least one uppercase, one lowercase, and one numerical character.</p>

                        </div>
                        <div class="sm:col-span-6">
                            <label for="child-name" class="block font-medium text-gray-900 text-sm/6">
                                Child's Name (Profile Display Name)</label>
                            <div class="mt-2">
                                <input v-model="request.childDisplayName"
                                       id="child-name" name="child-name" type="email" autocomplete="child-name"
                                       class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-electric-600 sm:text-sm/6"
                                       placeholder="Child's name">
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="flex items-center justify-between">
                    <div class="flex items-center">

                        <input v-model="request.agreeToNewsletters"
                               id="agreeToNewsletters"
                               name="agreeToNewsletters"
                               type="checkbox"
                               class="w-4 h-4 rounded border-clay-500 text-slate-800 focus:ring-slate-500">

                        <label for="agreeToNewsletters"
                               class="block ml-2 text-sm text-gray-900">
                            I want to receive e-mail updates with auditions, ticket discounts and theatre news.
                        </label>

                    </div>
                </div>

                <div>
                    <SaButton @click.prevent="createAccount" :loading="isSubmitting" variant="primary">
                        <template #loading>
                            Submitting...
                        </template>
                        Create Account
                    </SaButton>
                </div>
            </div>
        </div>
    </div>

</template>

<script lang="ts" setup>
// @ts-ignore
import {computed, defineProps, Ref, ref} from "vue";
import {client} from '@/shared';
import ErrorSummary from "@/main/components/ErrorSummary.vue";
import {CreateAccount, CreateAccountResponse, Region} from "@/dtos";
import SaButton from "@/main/components/SaButton.vue";

const props = defineProps({
    id: {
        type: String,
        default: ''
    },
    email: {
        type: String,
        default: ''
    },
    fromEu: {
        type: Boolean,
        default: false
    }
});

const agreeToAgeVerify = ref(false);
const agreeToCollectConsent = ref(false);
const agreeToCompliance = ref(false);
const agreeToRevokeConsent = ref(false);


const attemptedToSubmit = ref(false);
const isSubmitting = ref(false);
const request = ref(new CreateAccount({
    id: props.id,
    name: '',
    password: '',
    agreeToNewsletters: !props.fromEu,
    accountIsForChild: false,
    childDisplayName: '',
    agreeToAllChildAccountConsents: false
}));

const agreeToAllChildAccountConsents = computed(() =>
    agreeToAgeVerify.value &&
    agreeToCollectConsent.value &&
    agreeToCompliance.value &&
    agreeToRevokeConsent.value
);

const createSuccessful: Ref<Boolean> = ref(false);
const responseStatus = ref({});

const createAccount = async () => {
    attemptedToSubmit.value = true;

    isSubmitting.value = true;
    request.value.agreeToAllChildAccountConsents = agreeToAllChildAccountConsents;

    await client.post<CreateAccountResponse>(request.value)
        .then((response) => {
            createSuccessful.value = true;
            responseStatus.value = {};
            location.href = response.redirectToUrl;
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
    isSubmitting.value = false;
}

const passwordVisible = ref(false);
const togglePasswordVisibility = () => {
    passwordVisible.value = !passwordVisible.value;
};

</script>